import React, { useState, useEffect } from 'react';
import { Menu, X, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { NAV_ITEMS, openWhatsApp, scrollToSection } from '../constants';

const LanguageToggle = ({ language, toggleLanguage, className = '' }) => (
  <button
    onClick={toggleLanguage}
    data-testid="language-toggle"
    aria-label="Toggle language"
    className={`flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase ${className}`}
  >
    <span className={language === 'fr' ? 'text-bordeaux' : 'text-ink/40 hover:text-ink'}>FR</span>
    <span className="w-px h-3 bg-ink/20" />
    <span className={language === 'en' ? 'text-bordeaux' : 'text-ink/40 hover:text-ink'}>EN</span>
  </button>
);

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id) => {
    scrollToSection(id);
    setIsOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        isScrolled || isOpen ? 'bg-cream/85 backdrop-blur-xl border-b border-ink/5' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 h-24 md:h-28 flex items-center justify-between gap-6">
        <button onClick={() => go('home')} className="flex items-center shrink-0" data-testid="header-logo" aria-label="BiTExpress">
          <img src="/bitexpress-logo.png" alt="BiTExpress Logo" className="h-20 md:h-28 w-auto object-contain" />
        </button>

        <nav className="hidden lg:flex items-center gap-10" data-testid="desktop-nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => go(item.id)}
              data-testid={`nav-${item.id}`}
              className="nav-link text-xs font-semibold uppercase tracking-[0.18em] text-ink/70 hover:text-bordeaux"
            >
              {t.nav[item.key]}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-8">
          <LanguageToggle language={language} toggleLanguage={toggleLanguage} />
          <button
            onClick={openWhatsApp}
            data-testid="header-whatsapp-btn"
            className="inline-flex items-center gap-2 rounded-full bg-gold hover:bg-gold-dark active:scale-95 text-ink text-sm font-bold px-5 py-2.5"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={2} />
            {t.nav.whatsapp}
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-5">
          <LanguageToggle language={language} toggleLanguage={toggleLanguage} />
          <button
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            className="text-bordeaux"
          >
            {isOpen ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {isOpen && (
        <nav className="lg:hidden bg-cream border-t border-ink/5 px-6 py-6 flex flex-col" data-testid="mobile-nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => go(item.id)}
              data-testid={`mobile-nav-${item.id}`}
              className="flex items-center justify-between py-4 border-b border-ink/10 font-heading text-2xl text-ink hover:text-bordeaux text-left"
            >
              {t.nav[item.key]}
            </button>
          ))}
          <button
            onClick={openWhatsApp}
            data-testid="mobile-whatsapp-btn"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold hover:bg-gold-dark text-ink font-bold px-6 py-4"
          >
            <MessageCircle className="w-4 h-4" />
            {t.nav.whatsapp}
          </button>
        </nav>
      )}
    </header>
  );
};

export default Header;
